import React, { useEffect, useState } from 'react'
import { Form } from 'antd'
import Utils from './utils'
import SignersForm from './SignersForm/SignersForm'
import { NewOrderFormProps, FormValues } from './types'
import { FeeUpdatedAlert } from './AppointmentDetails/FeeUpdatedAlert'
import { snakeCase } from 'lodash'

const NewOrderForm: React.FC<NewOrderFormProps> = ({
  sections,
  order,
  current_company,
  authenticity_token,
  onValuesChange
}) => {
  const [form] = Form.useForm()
  const [ronSigning, setRonSigning] = useState<boolean>(false)
  const [fee, setFee] = useState<string>('')

  const adminDuplicateSection = sections.length === 5 ? sections[0] : null
  const remainingSections = sections.length === 5 ? sections.slice(1) : sections

  useEffect(() => {
    installHandleHamlChangeHandlers()
    initializeRonSigning()

    if (current_company.state_specific_pricing) {
      document.addEventListener('feeChanged', handleFeeChange)
    }

    return () => {
      if (current_company.state_specific_pricing) {
        document.removeEventListener('feeChanged', handleFeeChange)
      }
    }
  }, [])

  const initializeRonSigning = (): void => {
    const ronSigningElement = document.getElementById('order_ron_signing') as HTMLInputElement
    if (ronSigningElement) {
      setRonSigning(ronSigningElement.value === 'true')
    }
  }

  const handleFeeChange = (event: CustomEvent) => {
    const { fee: newFee } = event.detail
    if (newFee && newFee !== fee) {
      setFee(newFee)
    }
  }

  const installHandleHamlChangeHandlers = (): void => {
    if (typeof window !== 'undefined' && (window as any).$) {
      const $ = (window as any).$
      $(document).on('ajaxComplete', () => {
        setTimeout(() => initializeRonSigning(), 100)
      })
    }
  }

  const setInputValue = (name: string, value: string | number | boolean | null | undefined): void => {
    const element = document.querySelector(`input[name="${name}"]`) as HTMLInputElement
    if (element) {
      if (value === null || value === undefined) {
        element.value = ''
      } else {
        element.value = String(value)
      }
    }
  }

  const createHiddenSignerFields = (signers: any[]): void => {
    document.querySelectorAll('[name^="order[signers]"]').forEach((el) => {
      el.remove()
    })

    const form = document.getElementById('new_order')
    if (!form) return

    // Create hidden fields for all signers
    for (let i = 0; i < signers.length; i++) {
      const signer = { ...signers[i] }

      Object.entries(signer).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          const input = document.createElement('input')
          input.type = 'hidden'
          input.name = `order[signers][${i}][${snakeCase(key)}]`
          input.value = String(value)
          form.appendChild(input)
        }
      })
    }
  }

  const handleReactValuesChange = (changedValues: Partial<FormValues>, values: FormValues): void => {
    if ('signers' in changedValues && values.signers) {
      console.log('Signers changed:', values.signers)

      // Set primary signer values
      setInputValue('order[firstname]', values.signers[0]?.firstName)
      setInputValue('order[lastname]', values.signers[0]?.lastName)
      setInputValue('order[mobile_phone]', values.signers[0]?.cellPhone)
      setInputValue('order[home_phone]', values.signers[0]?.additionalPhone)
      setInputValue('order[signer_email]', values.signers[0]?.email)
      setInputValue('order[signer_allow_access_to_order]', values.signers[0]?.allowAccessToOrder ? 1 : 0)

      // Set co-signer values
      setInputValue('order[co_first_name]', values.signers[1]?.firstName)
      setInputValue('order[co_last_name]', values.signers[1]?.lastName)
      setInputValue('order[co_mobile_phone]', values.signers[1]?.cellPhone)
      setInputValue('order[co_home_phone]', values.signers[1]?.additionalPhone)
      setInputValue('order[co_signer_email]', values.signers[1]?.email)
      setInputValue('order[co_signer_allow_access_to_order]', values.signers[1]?.allowAccessToOrder ? 1 : 0)

      createHiddenSignerFields(values.signers)
    }

    // Call the optional onValuesChange prop
    onValuesChange?.(changedValues, values)
  }

  const initialSigners = Utils.generateInitialSigners(order)

  const orderWithDefaults: FormValues = {
    ...order,
    authenticity_token: authenticity_token,
    signers: initialSigners
  }
  console.log({ orderWithDefaults })

  // Create hidden fields for initial signers (handles form re-renders with errors)
  useEffect(() => {
    if (initialSigners.length > 0) {
      createHiddenSignerFields(initialSigners)
    }
  }, [initialSigners])

  const disabled = order?.closing_id !== undefined && order.closing_id !== null

  return (
    <Form
      onValuesChange={handleReactValuesChange}
      initialValues={orderWithDefaults}
      form={form}
      component={false}
      disabled={disabled}
    >
      <form
        className="order-form title-company-order-form"
        id="new_order"
        autoComplete="off"
        data-dynamic-selects-url="/orders/dynamic_selects"
        action={order.id ? `/orders/${order.id}` : '/orders'}
        acceptCharset="UTF-8"
        method="post"
        noValidate
      >
        {order.id && <input type="hidden" name="_method" value="patch" />}
        <input type="hidden" name="authenticity_token" value={authenticity_token} />
        <div className="container order-entry-container">
          {adminDuplicateSection && (
            <div
              style={{ display: 'table', width: '100%' }}
              dangerouslySetInnerHTML={{ __html: adminDuplicateSection }}
            />
          )}
          <div style={{ display: 'table', width: '100%' }} dangerouslySetInnerHTML={{ __html: remainingSections[0] }} />
          <div style={{ display: 'table', width: '100%' }} dangerouslySetInnerHTML={{ __html: remainingSections[1] }} />
          {!ronSigning && fee && (
            <div style={{ display: 'table', width: '100%', marginTop: '-8px' }}>
              <div className="row panel-sb">
                <div className="col-md-3 hidden-xs hidden-sm column-left" />
                <div className="col-md-6 col-xs-12 styled-element column-content">
                  <FeeUpdatedAlert fee={fee} />
                </div>
                <div className="col-md-3 hidden-xs hidden-sm column-right" />
              </div>
            </div>
          )}
          <div style={{ display: 'table', width: '100%' }} dangerouslySetInnerHTML={{ __html: remainingSections[2] }} />
          <div style={{ display: 'table', width: '100%' }}>
            <div className="row panel-sb">
              <div className="col-md-3 hidden-xs hidden-sm column-left">
                <div className="text-title">{current_company.is_title_company ? 'Consumer' : 'Signer'}</div>
              </div>
              <div className="col-md-6 col-xs-12 styled-element column-content">
                <div className="hidden-md hidden-lg">
                  <div className="text-title">{current_company.is_title_company ? 'Consumer' : 'Signer'}</div>
                </div>
                <SignersForm maxSigners={4} ronSigning={ronSigning} />
              </div>
              <div className="col-md-3 hidden-xs hidden-sm column-right" />
            </div>
          </div>

          <div style={{ display: 'table', width: '100%' }} dangerouslySetInnerHTML={{ __html: remainingSections[3] }} />
        </div>
      </form>
    </Form>
  )
}

export default NewOrderForm
